import React from "react";
import { useTheme } from "hooks";
import "./LegalPage.styles.scss";

export default function PrivacyPolicyPage() {
  const { theme } = useTheme();
  const accentColor = (theme && theme.color1) || "#0077b6";

  return (
    <div className="legal-page" style={{ "--legal-accent": accentColor }}>
      {/* ── Header ── */}
      <header className="legal-header">
        <div className="legal-header-inner">
          <div className="legal-header-brand">
            {theme && theme.logo ? (
              <img src={theme.logo} alt="Citrus eCF" />
            ) : (
              <span>Citrus eCF</span>
            )}
          </div>
          <span className="legal-header-tag">Privacidad</span>
        </div>
      </header>

      {/* ── Content ── */}
      <div className="legal-body">
        <div className="legal-container">

          {/* Document header */}
          <div className="legal-doc-header">
            <span className="legal-doc-badge">Privacy Policy</span>
            <h1>Política de Privacidad</h1>
            <p className="legal-subtitle">
              Citrus eCF – Integración con QuickBooks Online
            </p>
            <div className="legal-meta">
              <span><strong>Fecha de Vigencia:</strong> 26 de febrero de 2026</span>
              <span><strong>Última Actualización:</strong> 26 de febrero de 2026</span>
            </div>
          </div>

          {/* Section 1 */}
          <div className="legal-section">
            <h2>1. Introducción</h2>
            <p>
              Esta Política de Privacidad describe cómo{" "}
              <strong>Citrus Technology</strong> ("Citrus", "nosotros", "nos",
              "nuestro") recopila, usa, almacena y protege la información cuando
              usted utiliza la integración Citrus eCF – QuickBooks Online ("la
              Integración").
            </p>
            <p>
              Al conectar su cuenta de QuickBooks Online a Citrus eCF, usted
              consiente las prácticas descritas en esta política.
            </p>
          </div>

          {/* Section 2 */}
          <div className="legal-section">
            <h2>2. Datos que Accedemos desde QuickBooks Online</h2>
            <p>
              Cuando usted autoriza la Integración mediante OAuth 2.0, accedemos
              a los siguientes datos de su cuenta de QuickBooks Online,
              exclusivamente para realizar la función de cumplimiento fiscal
              electrónico (eCF):
            </p>

            <h3>2.1 Documentos Transaccionales</h3>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Tipo de Dato</th>
                    <th>Propósito</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Facturas (Invoices)</strong></td>
                    <td>Convertir a Facturas de Crédito Fiscal (eCF) para envío a la DGII</td>
                  </tr>
                  <tr>
                    <td><strong>Órdenes de Compra / Bills</strong></td>
                    <td>Convertir a Comprobantes de Compras (eCF) para envío a la DGII</td>
                  </tr>
                  <tr>
                    <td><strong>Notas de Crédito (Credit Memos)</strong></td>
                    <td>Convertir a Notas de Crédito (eCF) para envío a la DGII</td>
                  </tr>
                  <tr>
                    <td><strong>Pagos (Payments)</strong></td>
                    <td>Determinar montos de pago relacionados a notas de crédito</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>2.2 Datos de Referencia / Maestros</h3>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Tipo de Dato</th>
                    <th>Propósito</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Clientes</strong> (nombre, correo, teléfono/RNC)</td>
                    <td>Identificar al receptor fiscal del documento eCF</td>
                  </tr>
                  <tr>
                    <td><strong>Suplidores / Vendors</strong> (nombre, correo, teléfono/RNC)</td>
                    <td>Identificar al proveedor fiscal en documentos de compras</td>
                  </tr>
                  <tr>
                    <td><strong>Artículos / Items</strong> (nombre, precio, configuración de impuestos)</td>
                    <td>Completar las líneas de detalle en los documentos eCF</td>
                  </tr>
                  <tr>
                    <td><strong>Tasas de Impuesto y Códigos Fiscales</strong></td>
                    <td>Calcular los valores de ITBIS y retenciones de ISR</td>
                  </tr>
                  <tr>
                    <td><strong>Información de la Empresa</strong> (nombre, razón social, correo)</td>
                    <td>Verificar la identidad de la empresa de QuickBooks vinculada</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="legal-notice">
              <strong>Nota sobre el RNC:</strong> La integración lee los números de
              identificación fiscal (RNC/cédula) de clientes y suplidores desde el
              campo <em>AlternatePhone</em> de los registros de contacto en QBO,
              que es el campo configurado para almacenar este dato.
            </div>
          </div>

          {/* Section 3 */}
          <div className="legal-section">
            <h2>3. Datos de Autenticación que Almacenamos</h2>
            <p>
              Para mantener la conexión con su cuenta de QuickBooks Online,
              almacenamos de forma segura las siguientes credenciales OAuth 2.0
              en nuestra base de datos:
            </p>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Descripción</th>
                    <th>Retención</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>AccessToken</strong></td>
                    <td>Token de portador de corta duración para llamadas a la API</td>
                    <td>Válido ~1 hora; reemplazado en cada renovación</td>
                  </tr>
                  <tr>
                    <td><strong>RefreshToken</strong></td>
                    <td>Token de larga duración para obtener nuevos access tokens</td>
                    <td>Válido ~101 días; reemplazado en cada uso</td>
                  </tr>
                  <tr>
                    <td><strong>RealmId</strong></td>
                    <td>ID de su empresa/organización en QuickBooks Online</td>
                    <td>Hasta desconectar la cuenta</td>
                  </tr>
                  <tr>
                    <td><strong>Fechas de expiración</strong></td>
                    <td>Marcas de tiempo para gestión automática de tokens</td>
                    <td>Hasta desconectar la cuenta</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Estos datos se almacenan como un JSON cifrado en nuestra base de
              datos SQL Server y están asociados exclusivamente con su cuenta de
              empresa en Citrus eCF.
            </p>
            <div className="legal-notice legal-warning">
              <strong>Importante:</strong> No almacenamos su nombre de usuario,
              contraseña ni ninguna otra credencial de inicio de sesión de
              QuickBooks Online o Intuit.
            </div>
          </div>

          {/* Section 4 */}
          <div className="legal-section">
            <h2>4. Datos que Escribimos de Vuelta en QuickBooks Online</h2>
            <p>
              Después de procesar cada documento, la Integración escribe los
              siguientes datos en el registro correspondiente de QBO como{" "}
              <strong>campos personalizados</strong> y{" "}
              <strong>archivos adjuntos</strong>:
            </p>
            <ul>
              <li>
                <strong>NCF</strong> – El número de comprobante fiscal electrónico
                asignado por la DGII.
              </li>
              <li>
                <strong>Fecha Vencimiento NCF</strong> – La fecha de expiración
                del NCF.
              </li>
              <li>
                <strong>TransaccionId</strong> – El identificador interno de la
                transacción en Citrus eCF.
              </li>
              <li>
                <strong>Tipo Comprobante Electronico</strong> – El tipo de
                documento eCF generado.
              </li>
              <li>
                <strong>Retencion ISR / Retencion ITBIS</strong> – Montos de
                retención (cuando aplica).
              </li>
              <li>
                <strong>Adjunto PDF</strong> – Representación legible por humanos
                del documento eCF.
              </li>
              <li>
                <strong>Adjunto XML</strong> – Documento eCF firmado digitalmente
                según requerimientos de la DGII.
              </li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="legal-section">
            <h2>5. Cómo Usamos sus Datos</h2>
            <p>
              Utilizamos los datos accedidos desde QuickBooks Online{" "}
              <strong>exclusivamente</strong> para los siguientes propósitos:
            </p>
            <ol>
              <li>
                <strong>Generación de eCF:</strong> Transformar sus documentos
                de QBO al formato XML requerido por la DGII.
              </li>
              <li>
                <strong>Envío a la DGII:</strong> Transmitir los documentos eCF
                a la autoridad tributaria dominicana para su registro y
                validación.
              </li>
              <li>
                <strong>Archivo de evidencia:</strong> Adjuntar el XML y PDF
                resultantes a sus registros en QBO como comprobante del
                documento electrónico.
              </li>
              <li>
                <strong>Reportes de cumplimiento:</strong> Generar informes
                fiscales y registros de auditoría dentro de su cuenta de
                Citrus eCF.
              </li>
              <li>
                <strong>Gestión de tokens:</strong> Renovar los tokens OAuth
                para mantener el servicio sin interrupciones.
              </li>
            </ol>
            <div className="legal-notice">
              <strong>No utilizamos</strong> sus datos de QuickBooks para
              marketing, publicidad, venta de datos, entrenamiento de modelos de
              inteligencia artificial ni para ningún propósito ajeno al
              cumplimiento fiscal eCF.
            </div>
          </div>

          {/* Section 6 */}
          <div className="legal-section">
            <h2>6. Compartición de Datos</h2>
            <p>
              Compartimos sus datos únicamente cuando es estrictamente necesario:
            </p>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Destinatario</th>
                    <th>Datos Compartidos</th>
                    <th>Propósito</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>DGII (República Dominicana)</strong></td>
                    <td>Documentos XML eCF, datos fiscales</td>
                    <td>Obligación legal de cumplimiento tributario</td>
                  </tr>
                  <tr>
                    <td><strong>Intuit / API de QuickBooks</strong></td>
                    <td>Tokens OAuth (para llamadas a la API)</td>
                    <td>Acceder a los datos de QBO autorizados por usted</td>
                  </tr>
                  <tr>
                    <td><strong>Azure File Share</strong> (si está configurado)</td>
                    <td>Archivos XML/PDF del eCF</td>
                    <td>Almacenamiento seguro y archivo de documentos</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              No vendemos, alquilamos ni compartimos sus datos de QuickBooks con
              ningún tercero con fines comerciales o de marketing.
            </p>
          </div>

          {/* Section 7 */}
          <div className="legal-section">
            <h2>7. Seguridad de los Datos</h2>
            <p>
              Implementamos las siguientes medidas de seguridad para proteger su
              información:
            </p>
            <ul>
              <li>
                <strong>TLS 1.2</strong> para todos los datos en tránsito entre
                Citrus eCF, QuickBooks Online y la DGII.
              </li>
              <li>
                <strong>Conexiones cifradas a la base de datos</strong> para
                todos los tokens OAuth y datos fiscales almacenados.
              </li>
              <li>
                <strong>Firmas digitales</strong> en todos los documentos eCF
                mediante certificados X.509 según los requerimientos de la DGII.
              </li>
              <li>
                <strong>Controles de acceso</strong> – Solo los usuarios
                autorizados dentro de su cuenta de empresa en Citrus eCF pueden
                ver o gestionar la conexión de QuickBooks.
              </li>
              <li>
                <strong>Aislamiento de tokens</strong> – Los tokens OAuth se
                almacenan por empresa y nunca son accesibles por otras empresas
                en la plataforma.
              </li>
            </ul>
          </div>

          {/* Section 8 */}
          <div className="legal-section">
            <h2>8. Retención de Datos</h2>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Tipo de Dato</th>
                    <th>Período de Retención</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Tokens OAuth</strong> (AccessToken, RefreshToken)</td>
                    <td>
                      Hasta que desconecte la integración con QuickBooks o se
                      termine su cuenta de Citrus eCF
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Documentos eCF y datos fiscales</strong></td>
                    <td>
                      Según la legislación tributaria dominicana (mínimo 10 años
                      conforme a las regulaciones de la DGII)
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Datos transaccionales de QBO</strong> (clientes,
                      facturas, etc.)
                    </td>
                    <td>
                      Usados transitoriamente durante el procesamiento; no se
                      retienen más allá de lo incorporado en el registro eCF
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Al desconectar su cuenta de QuickBooks ("Desvincular"), los tokens
              OAuth almacenados se eliminan de inmediato de nuestra base de datos.
            </p>
          </div>

          {/* Section 9 */}
          <div className="legal-section">
            <h2>9. Sus Derechos y Controles</h2>
            <p>
              Usted dispone de los siguientes controles sobre la Integración:
            </p>
            <ul>
              <li>
                <strong>Conectar / Desconectar en cualquier momento</strong> desde
                la configuración de "Sistemas Externos" en la plataforma Citrus
                eCF.
              </li>
              <li>
                <strong>Revocar el acceso directamente</strong> desde su cuenta de{" "}
                <a
                  href="https://developer.intuit.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: accentColor }}
                >
                  Intuit Developer
                </a>{" "}
                o desde la configuración de QuickBooks Online en cualquier momento.
              </li>
              <li>
                <strong>Solicitar la eliminación de datos</strong> contactándonos
                en la dirección indicada al final de esta política; eliminaremos
                todos los tokens OAuth almacenados y los datos originados en QBO
                que no sean requeridos por retención legal tributaria.
              </li>
              <li>
                <strong>Acceder a sus datos:</strong> Todos los documentos eCF
                generados a partir de sus datos de QBO son accesibles desde el
                panel de su cuenta Citrus eCF.
              </li>
            </ul>
          </div>

          {/* Section 10 */}
          <div className="legal-section">
            <h2>10. Alcance del Acceso</h2>
            <p>
              La Integración solicita el alcance OAuth 2.0 de{" "}
              <strong>Accounting</strong> de QuickBooks Online. Este alcance
              provee acceso de lectura y escritura a sus datos contables de QBO
              tal como se describe en las secciones 2 y 4. No solicitamos el
              alcance de Nómina (Payroll), Pagos (Payments) ni ningún otro
              alcance de QuickBooks más allá de lo necesario para el
              procesamiento de eCF.
            </p>
          </div>

          {/* Section 11 */}
          <div className="legal-section">
            <h2>11. Privacidad de Menores</h2>
            <p>
              La Integración está diseñada para uso empresarial y no está
              dirigida a personas menores de 18 años. No recopilamos a
              sabiendas información personal de menores de edad.
            </p>
          </div>

          {/* Section 12 */}
          <div className="legal-section">
            <h2>12. Cambios en esta Política</h2>
            <p>
              Podemos actualizar esta Política de Privacidad periódicamente. Le
              notificaremos los cambios materiales a través de la plataforma
              Citrus eCF o por correo electrónico. El uso continuado de la
              Integración tras la fecha de vigencia constituye la aceptación de
              la política actualizada.
            </p>
          </div>

          {/* Section 13 */}
          <div className="legal-section">
            <h2>13. Contáctenos</h2>
            <p>
              Para preguntas relacionadas con privacidad, solicitudes de
              eliminación de datos o inquietudes sobre esta política, contáctenos
              en:
            </p>
            <p>
              <strong>Citrus Technology</strong>
              <br />
              Correo electrónico:{" "}
              <a
                href="mailto:soporte@citrus.com.do"
                style={{ color: accentColor }}
              >
                soporte@citrus.com.do
              </a>
              <br />
              Sitio web:{" "}
              <a
                href="https://citrus.com.do"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: accentColor }}
              >
                citrus.com.do
              </a>
            </p>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <a href="/" className="legal-back-link">
              &#8592; Volver al inicio
            </a>
            <div className="legal-nav-links">
              <a href="/eula">Acuerdo de Licencia (EULA)</a>
              <a href="/quickbooks-integration">Integración QuickBooks</a>
            </div>
            <p className="legal-copyright">
              &copy; {new Date().getFullYear()} Citrus Technology. Todos los
              derechos reservados.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
